import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import notesMarkdown from "../../../documentation/release-notes.md?raw";
import { LMS_VERSION, vtag, type RuntimeConfig } from "../config";
import { Card, Inlines, PageHeader } from "../components/ui";
import { parseReleaseNotes } from "../lib/release-notes";

/**
 * The product's own changelog, rendered from the file that is the record
 * (documentation/release-notes.md) and bundled at build time, so what a
 * person reads here is exactly what shipped. The footer links to
 * `#v<version>` for the build they are on. `Unreleased` — merged but not
 * yet numbered — shows everywhere but prod (release-notes template: staff
 * see it, a learner never does; the role check arrives with the matrix).
 */
export function ReleaseNotesPage({ config }: { config: RuntimeConfig }) {
  const all = useMemo(() => parseReleaseNotes(notesMarkdown).versions, []);
  const versions = config.envName === "prod" ? all.filter((v) => v.id !== "unreleased") : all;
  const current = vtag(LMS_VERSION);
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    target.scrollIntoView({ block: "start" });
    target.focus({ preventScroll: true });
  }, [hash]);

  return (
    <>
      <PageHeader title="Release notes" subtitle={`What changed in each version. You are on ${current}.`} />
      <div className="space-y-6">
        {versions.map((v) => (
          <Card key={v.id}>
            <h2 id={v.id} tabIndex={-1} className="text-xl font-semibold text-ink">
              {v.version === "Unreleased" ? "Unreleased" : `v${v.version}`}
              {v.date && <span className="ml-2 text-base font-normal text-muted">{v.date}</span>}
              {v.id === current && <span className="ml-2 rounded-full border border-accent px-2 py-0.5 text-sm font-medium text-accent">this build</span>}
            </h2>
            {v.intro.map((p, i) => (
              <p key={i} className="mt-2 text-muted">
                <Inlines parts={p} />
              </p>
            ))}
            {v.sections.length === 0 && v.intro.length === 0 && <p className="mt-2 text-muted">Nothing yet.</p>}
            {v.sections.map((s) => (
              <div key={s.heading || "bullets"} className="mt-4">
                {s.heading && <h3 className="text-sm font-semibold uppercase tracking-wide text-muted">{s.heading}</h3>}
                <ul className="mt-2 list-disc space-y-2 pl-5">
                  {s.bullets.map((b, i) => (
                    <li key={i}>
                      <Inlines parts={b} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Card>
        ))}
      </div>
    </>
  );
}
